import React from 'react';
import Icon from '../Icon';
import usePageMeta from '../usePageMeta';
import { ANTCZAK_GROUP, PARTNERS } from '../siteData';
import TeamGrid from '../components/TeamGrid';
import T from '../components/EditableText';
import PageHero from '../components/PageHero';
import CtaBand from '../components/CtaBand';
import { useSiteContent } from '../SiteContent';

/**
 * Symbol je Merkmal; Überschrift und Text stehen als `ueberuns.wert-N.…` in
 * `content/ueberuns.ts`. Die Reihenfolge hier bestimmt die Reihenfolge auf der Seite.
 */
const VALUE_ICONS = [
  'map-pin',
  'radar',
  'house',
  'clipboard-list',
  'building-2',
  'zap',
  'languages',
  'file-check',
  'heart-handshake',
];

const UeberUns: React.FC = () => {
  const { t } = useSiteContent();

  usePageMeta(
    'Über uns – Seniorenbetreuung Antczak in Gifhorn',
    'Wer hinter der Seniorenbetreuung Antczak steht: das Team aus Gifhorn, unsere Werte und die Pflegedienste, mit denen wir in der Region zusammenarbeiten.',
    { standaloneTitle: true },
  );

  return (
    <>
      <PageHero
        eyebrowKey="ueberuns.hero.eyebrow"
        titleKey="ueberuns.hero.titel"
        accentKey="ueberuns.hero.titel-akzent"
        image={{ src: '/website/img/roman-portrait.jpg', alt: 'Roman Antczak, Inhaber der Seniorenbetreuung Antczak', width: 720, height: 900, position: 'center 20%' }}
      >
        <p className="hx-hero__lead">
          <T k="ueberuns.hero.absatz-1" />
        </p>
      </PageHero>

      <section className="shell section">
        <div className="prose hx-lede">
          <p>
            <T k="ueberuns.hero.absatz-2" />
          </p>
          <p>
            <T k="ueberuns.hero.absatz-3" />
          </p>
        </div>
      </section>

      {/* Werte als ruhiges 3×3-Raster mit Haarlinien */}
      <section className="shell section--tight">
        <div className="hx-values">
          {VALUE_ICONS.map((icon, index) => (
            <div className="hx-value" key={icon}>
              <Icon name={icon} size={20} />
              <h2 className="h-card">
                <T k={`ueberuns.wert-${index + 1}.titel`} />
              </h2>
              <p>
                <T k={`ueberuns.wert-${index + 1}.text`} />
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="section--tinted">
        <div className="shell section">
          <div className="hx-steps__head">
            <h2 className="h-section">
              <T k="ueberuns.team.titel" />
            </h2>
            <p>
              <T k="ueberuns.team.text" />
            </p>
          </div>
          <TeamGrid />
        </div>
      </section>

      {/* Antczak Group */}
      <section className="shell" style={{ paddingTop: 44, paddingBottom: 10 }}>
        <h2 className="h-block" style={{ marginBottom: 20 }}>
          <T k="ueberuns.gruppe.titel" />
        </h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
          {ANTCZAK_GROUP.map((item, index) => (
            <img
              key={item.image}
              className="logo-tile"
              src={item.image}
              // Der Name steht im Alternativtext und im Tooltip, ist also keine
              // sichtbare Textstelle — deshalb über t() statt über <T>.
              alt={t(`gruppe.${index + 1}.name`)}
              title={t(`gruppe.${index + 1}.name`)}
              width={96}
              height={96}
              style={{ width: 96, height: 96, objectFit: 'cover' }}
              loading="lazy"
            />
          ))}
        </div>
      </section>

      {/* Partner */}
      <section id="partner" className="shell section" style={{ scrollMarginTop: 90 }}>
        <h2 className="h-block" style={{ marginBottom: 8 }}>
          <T k="ueberuns.partner.titel" />
        </h2>
        <p className="hx-muted-lead" style={{ maxWidth: 640 }}>
          <T k="ueberuns.partner.text" />
        </p>
        <div className="autogrid" style={{ ['--min' as string]: '160px', gap: 16 }}>
          {PARTNERS.map((partner, index) => (
            <img
              key={partner.image}
              className="logo-tile"
              src={partner.image}
              alt={t(`partner.${index + 1}.name`)}
              title={t(`partner.${index + 1}.name`)}
              width={320}
              height={180}
              style={{ width: '100%', height: 'auto' }}
              loading="lazy"
            />
          ))}
        </div>
      </section>

      <CtaBand titleKey="ueberuns.cta.titel" textKey="ueberuns.cta.text" ctaKey="ueberuns.cta.schaltflaeche" />
      <div style={{ height: 48 }} />
    </>
  );
};

export default UeberUns;
